import { VALXML_ATTRIBUTE_DEFINITIONS } from 'ComponentLibrary/src/playground/tools/ValXmlExtractor/valxml-schema'

export const GENERAL_SECTION_ORDER = [
    'Gutachter & Besichtigung',
    'Bewertungsergebnisse',
    'Kosten & Abzüge',
    'Adresse & Eigentümer',
    'Weitere Kennzahlen',
] as const

export type GeneralSectionLabel = (typeof GENERAL_SECTION_ORDER)[number]

const GENERAL_SECTION_DEFAULT: GeneralSectionLabel = 'Weitere Kennzahlen'

export const classifyGeneralSection = (normalizedPath?: string): GeneralSectionLabel => {
    if (!normalizedPath) {
        return GENERAL_SECTION_DEFAULT
    }
    const lower = normalizedPath.toLowerCase()
    if (lower.includes('valuer') || lower.includes('inspection')) {
        return 'Gutachter & Besichtigung'
    }
    if (lower.startsWith('biisvaluationdata.general.address') || lower.includes('objnoowner') || lower.includes('owner')) {
        return 'Adresse & Eigentümer'
    }
    if (
        lower.includes('marketvalue') ||
        lower.includes('incomeapproach') ||
        lower.includes('costapproach') ||
        lower.includes('landvalue') ||
        lower.includes('capitalizationrate')
    ) {
        return 'Bewertungsergebnisse'
    }
    if (
        lower.includes('cost') ||
        lower.includes('expense') ||
        lower.includes('allowance') ||
        lower.includes('deduction') ||
        lower.includes('premium') ||
        lower.includes('groundrent') ||
        lower.includes('maintenance') ||
        lower.includes('management')
    ) {
        return 'Kosten & Abzüge'
    }
    return GENERAL_SECTION_DEFAULT
}

const isLeafPath = (candidate: string): boolean =>
    !VALXML_ATTRIBUTE_DEFINITIONS.some((other) => other.path !== candidate && other.path.startsWith(`${candidate}.`))

const leafPaths = VALXML_ATTRIBUTE_DEFINITIONS.filter((definition) => isLeafPath(definition.path)).map((definition) => definition.path)

const RENTAL_FIELD_SUFFIXES = [
    'ContractualAnnualRent',
    'EstimatedAnnualRentForLetArea',
    'EstimatedAnnualRentForVacantArea',
    'EstimatedAnnualRentForLetNumbers',
    'EstimatedAnnualRentForVacantNumbers',
    'EstimatedAnnualRentLet',
    'EstimatedAnnualRentVacant',
    'LetArea',
    'LetNumbers',
    'VacantArea',
    'VacantNumbers',
] as const

type CoverageGroup = { key: string; label: string; paths: string[] }

const RENTAL_GROUP_LABEL_TRANSLATIONS: Record<string, string> = {
    Archive: 'Archiv',
    BusinessGeneral: 'Gewerbe',
    Gastro: 'Gastronomie',
    Hotel: 'Hotel',
    Indoorparking: 'Innenstellplätze',
    Leisure: 'Freizeit',
    Office: 'Büro',
    Outsideparking: 'Außenstellplätze',
    Residential: 'Wohnen',
    Retail: 'Handel',
    Social: 'Sozial',
    Storage: 'Lager',
    Misc: 'Sonstige Nutzung',
}

export const RENTAL_SITUATION_GROUPS: CoverageGroup[] = (() => {
    const groups = new Map<string, Set<string>>()
    const relevant = VALXML_ATTRIBUTE_DEFINITIONS.filter(
        (definition) => leafPaths.includes(definition.path) && definition.path.includes('ValuationResults.RentalSituation')
    )

    relevant.forEach((definition) => {
        const suffix = definition.path.split('RentalSituation', 2)[1] ?? ''
        const matchedSuffix = RENTAL_FIELD_SUFFIXES.find((candidate) => suffix.endsWith(candidate))
        if (!matchedSuffix) {
            return
        }
        const rawGroupKey = suffix.slice(0, -matchedSuffix.length)
        if (!rawGroupKey) {
            return
        }
        const groupKey = rawGroupKey.startsWith('Misc') ? 'Misc' : rawGroupKey
        const store = groups.get(groupKey) ?? new Set<string>()
        store.add(definition.path)
        groups.set(groupKey, store)
    })

    return Array.from(groups.entries())
        .map(([key, paths]) => ({
            key,
            label: RENTAL_GROUP_LABEL_TRANSLATIONS[key] ?? key,
            paths: Array.from(paths).sort(),
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
})()
